import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { FileText, Plus, Upload, Download } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DailyReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [unitFilter, setUnitFilter] = useState("all");
  const [form, setForm] = useState({ title: "", report_type: "production", unit: "Unit 1", notes: "" });
  const [reportFile, setReportFile] = useState(null);

  const fetchReports = async () => {
    try {
      const params = unitFilter !== "all" ? `?unit=${unitFilter}` : "";
      const res = await authFetch(`${API}/factory/daily-reports${params}`);
      const data = await res.json();
      if (data.success) setReports(data.data || []);
    } catch { toast.error("Failed to load reports"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReports(); }, [unitFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      let documentUrl = "";
      if (reportFile) {
        const formData = new FormData();
        formData.append("file", reportFile);
        const uploadRes = await authFetch(`${API}/upload?category=daily-reports`, { method: "POST", body: formData });
        const uploadData = await uploadRes.json();
        if (uploadData.success) documentUrl = uploadData.data.storage_path;
      }
      
      const body = { ...form, document_url: documentUrl, date: new Date().toISOString().split("T")[0] };
      const res = await authFetch(`${API}/factory/daily-reports`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Report uploaded");
        setShowAdd(false);
        setForm({ title: "", report_type: "production", unit: "Unit 1", notes: "" });
        setReportFile(null);
        fetchReports();
      } else toast.error(data.detail || "Failed");
    } catch { toast.error("Network error"); }
    finally { setCreating(false); }
  };

  const REPORT_TYPES = { production: "Production", quality: "Quality Test", maintenance: "Maintenance", safety: "Safety", environmental: "Environmental" };

  return (
    <div className="space-y-6" data-testid="daily-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Sora']">Daily Reports</h1>
          <p className="text-emerald-600 text-sm mt-1">Upload and track daily technical reports</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-report-btn">
          <Plus className="w-4 h-4 mr-2" /> Upload Report
        </Button>
      </div>

      <div className="flex gap-3">
        <Select value={unitFilter} onValueChange={setUnitFilter}>
          <SelectTrigger className="w-40 bg-[#1C2B2B] border-emerald-900/50 text-white" data-testid="unit-filter">
            <SelectValue placeholder="All Units" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Units</SelectItem>
            <SelectItem value="Unit 1">Unit 1</SelectItem>
            <SelectItem value="Unit 2">Unit 2</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-3">
        {loading ? (
          <div className="text-center py-12"><div className="w-8 h-8 spinner mx-auto" /></div>
        ) : reports.length === 0 ? (
          <Card className="bg-[#1C2B2B] border-emerald-900/30 p-8 text-center">
            <FileText className="w-10 h-10 text-emerald-700 mx-auto mb-3" />
            <p className="text-slate-400">No reports uploaded yet</p>
          </Card>
        ) : reports.map((r, i) => (
          <Card key={r.report_id || i} className="bg-[#1C2B2B] border-emerald-900/30 p-4 hover:border-emerald-700/50 transition-colors" data-testid={`report-card-${i}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-900/30 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{r.title || "Report"}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge className="bg-emerald-900/30 text-emerald-400 text-[10px]">{REPORT_TYPES[r.report_type] || r.report_type}</Badge>
                    <Badge variant="outline" className="text-[10px] border-emerald-700 text-emerald-400">{r.unit || "Unit 1"}</Badge>
                    <span className="text-xs text-slate-500">{r.date}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {r.document_url && (
                  <Badge className="bg-blue-900/30 text-blue-400 text-[10px]">File Attached</Badge>
                )}
                <span className="text-xs text-slate-500">{r.uploader || "Unknown"}</span>
              </div>
            </div>
            {r.notes && <p className="text-xs text-slate-400 mt-2 ml-13">{r.notes}</p>}
          </Card>
        ))}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md bg-[#1A2332] border-emerald-900/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-['Sora']">Upload Daily Report</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div>
              <label className="text-xs text-emerald-600 font-medium">Title</label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" placeholder="e.g. Unit 1 Production Report" data-testid="report-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-emerald-600 font-medium">Type</label>
                <Select value={form.report_type} onValueChange={(v) => setForm({...form, report_type: v})}>
                  <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="report-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(REPORT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Unit</label>
                <Select value={form.unit} onValueChange={(v) => setForm({...form, unit: v})}>
                  <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Unit 1">Unit 1</SelectItem><SelectItem value="Unit 2">Unit 2</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-emerald-600 font-medium">Report File</label>
              <label className="flex items-center gap-2 mt-1 px-3 py-3 border border-dashed border-emerald-700 rounded-md cursor-pointer hover:border-emerald-500 bg-[#1C2B2B]">
                <Upload className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-slate-400">{reportFile ? reportFile.name : "Upload PDF, image, or document"}</span>
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.docx" onChange={(e) => setReportFile(e.target.files?.[0])} data-testid="report-file-upload" />
              </label>
            </div>
            <div>
              <label className="text-xs text-emerald-600 font-medium">Notes</label>
              <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" placeholder="Optional notes" data-testid="report-notes" />
            </div>
            <Button type="submit" disabled={creating} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-report-btn">
              {creating ? "Uploading..." : "Upload Report"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyReportsPage;
